import { firstEvidenceMonth } from "encamp-shared/src/waste/generatorFindings";

/** "Feb 2026"-style month label; UTC so the first-of-month date can't shift. */
export function formatPeriodMonth(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    timeZone: "UTC",
  });
}

type PeriodEvidence = {
  periodStart: string;
  lastPeriodStart?: string | null;
  mismatchPeriodStarts?: readonly string[] | null;
};

export function formatPeriodSpan(row: PeriodEvidence): string {
  const first = formatPeriodMonth(firstEvidenceMonth(row));
  const last = row.lastPeriodStart ? formatPeriodMonth(row.lastPeriodStart) : first;
  return first === last ? first : `${first} – ${last}`;
}

/** Span of the comparisons' mismatch months, so it agrees with their month counts. */
export function formatComparisonPeriod(
  comparisons: readonly { earliestMismatch?: string | null; latestMismatch?: string | null }[]
): string | null {
  const earliest = comparisons.flatMap((c) => c.earliestMismatch ?? []).sort()[0];
  const latest = comparisons
    .flatMap((c) => c.latestMismatch ?? [])
    .sort()
    .at(-1);
  if (earliest == null || latest == null) return null;
  return formatPeriodSpan({ periodStart: earliest, lastPeriodStart: latest });
}

function monthIndex(iso: string): number {
  const d = new Date(iso);
  return d.getUTCFullYear() * 12 + d.getUTCMonth();
}

/** Calendar months covered by the evidence, inclusive. */
export function monthsInSpan(row: PeriodEvidence): number {
  if (!row.lastPeriodStart) return 1;
  return monthIndex(row.lastPeriodStart) - monthIndex(firstEvidenceMonth(row)) + 1;
}

function formatMonthOnly(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", { month: "short", timeZone: "UTC" });
}

function mismatchMonthRanges(isoMonths: readonly string[]): { start: string; end: string }[] {
  const sorted = [...isoMonths].sort((a, b) => monthIndex(a) - monthIndex(b));
  const ranges: { start: string; end: string }[] = [];
  for (const iso of sorted) {
    const last = ranges[ranges.length - 1];
    if (last && monthIndex(iso) === monthIndex(last.end) + 1) last.end = iso;
    else ranges.push({ start: iso, end: iso });
  }
  return ranges;
}

/** Consecutive months collapsed into labeled ranges, oldest first. */
export function formatMismatchMonthRanges(isoMonths: readonly string[]): string[] {
  return mismatchMonthRanges(isoMonths).map(({ start, end }) => {
    if (start === end) return formatPeriodMonth(start);
    const sameYear = new Date(start).getUTCFullYear() === new Date(end).getUTCFullYear();
    if (sameYear) return `${formatMonthOnly(start)}–${formatPeriodMonth(end)}`;
    return `${formatPeriodMonth(start)} – ${formatPeriodMonth(end)}`;
  });
}

/** Each year lists only its detected months, with consecutive months compacted. */
export function formatMismatchMonthsByYear(
  isoMonths: readonly string[]
): { year: number; months: string }[] {
  const byYear = new Map<number, string[]>();
  for (const iso of isoMonths) {
    const year = new Date(iso).getUTCFullYear();
    const months = byYear.get(year) ?? [];
    months.push(iso);
    byYear.set(year, months);
  }
  return [...byYear.entries()]
    .sort(([a], [b]) => a - b)
    .map(([year, months]) => ({
      year,
      months: mismatchMonthRanges(months)
        .map(({ start, end }) =>
          start === end
            ? formatMonthOnly(start)
            : `${formatMonthOnly(start)}–${formatMonthOnly(end)}`
        )
        .join(", "),
    }));
}

export function formatMismatchMonths(isoMonths: readonly string[]): string {
  return formatMismatchMonthRanges(isoMonths).join(" · ");
}
